import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {restrictToVerticalAxis} from '@dnd-kit/modifiers';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {Search} from 'platform/components';
import {Box, Center, Spinner, Text} from 'platform/foundation';
import styled from 'styled-components';
import {match, Pattern} from 'ts-pattern';

import {useState, type KeyboardEventHandler, type ReactNode} from 'react';

import {isNil, trim} from 'ramda';
import {isNotNilOrEmpty} from 'ramda-adjunct';

import {
  RelativeFilterResponseBody,
  useGetRelativeFiltersQuery,
  useReorderRelativeFiltersMutation,
} from '@dms/api/core';
import i18n from '@dms/i18n';
import {handleApiError} from '@dms/shared';

import {suffixTestId, TestIdProps} from 'shared/web';

import {AdServerRelativeFiltersDropdownFooter} from './components/AdServerRelativeFiltersDropdownFooter';
import {RelativeFilterPresetListItem} from './components/RelativeFilterPresetListItem';
import {buildReorderRelativeFiltersBody} from './utils/buildReorderRelativeFiltersBody';
import {filterPresetsBySearch} from './utils/filterPresetsBySearch';
import {reorderPresetsInList} from './utils/reorderPresetsInList';
import {sortPresetsForDropdown} from './utils/sortPresetsForDropdown';

const KEYS_THAT_SHOULD_PROPAGATE = new Set(['Escape', 'Tab', 'ArrowUp', 'ArrowDown']);

const handleDropdownStickyRegionKeyDown: KeyboardEventHandler<HTMLDivElement> = (event) => {
  const isFocusedInside = event.currentTarget.contains(document.activeElement);

  if (isFocusedInside && !KEYS_THAT_SHOULD_PROPAGATE.has(event.key)) {
    event.stopPropagation();
  }
};

interface AdServerRelativeFiltersDropdownProps extends TestIdProps {
  onOpenWithPreset: (presetId: string) => void;
  onCreatePreset: () => void;
  onEditPreset: (presetId: string) => void;
  onClose: () => void;
}

export function AdServerRelativeFiltersDropdown(props: AdServerRelativeFiltersDropdownProps) {
  const {onOpenWithPreset, onCreatePreset, onEditPreset, onClose} = props;

  const [searchQuery, setSearchQuery] = useState<string | null>(null);
  const [reorderRelativeFilters] = useReorderRelativeFiltersMutation();
  const {data: presets = [], isError, isLoading, refetch} = useGetRelativeFiltersQuery();

  const orderedPresets = sortPresetsForDropdown(presets);
  const filteredPresets = filterPresetsBySearch(orderedPresets, searchQuery);
  const sortablePresetIds = orderedPresets
    .filter((preset) => !preset.default)
    .map((preset) => preset.id);
  const isDnDEnabled = !isNotNilOrEmpty(trim(searchQuery ?? ''));

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleClose = () => {
    setSearchQuery(null);
    onClose();
  };

  const handleOpenPreset = (presetId: string) => {
    onOpenWithPreset(presetId);
    handleClose();
  };

  const handleCreatePreset = () => {
    onCreatePreset();
    handleClose();
  };

  const handleEditPreset = (presetId: string) => {
    onEditPreset(presetId);
    handleClose();
  };

  const handleDragEnd = (event: DragEndEvent) => {
    if (!isDnDEnabled) {
      return;
    }

    const {active, over} = event;

    if (isNil(over) || active.id === over.id) {
      return;
    }

    const reorderedPresets = reorderPresetsInList(
      orderedPresets,
      String(active.id),
      String(over.id)
    );

    reorderRelativeFilters({
      reorderRelativeFiltersRequestBody: buildReorderRelativeFiltersBody(reorderedPresets),
    })
      .unwrap()
      .catch(handleApiError);
  };

  const buildPresetRow = (preset: RelativeFilterResponseBody) => {
    const listItemProps = {
      preset,
      onOpen: () => handleOpenPreset(preset.id),
      onEdit: () => handleEditPreset(preset.id),
      'data-testid': suffixTestId(`preset-${preset.id}`, props),
    };

    return (
      <RelativeFilterPresetListItem
        key={preset.id}
        {...listItemProps}
        isSortable={isDnDEnabled && !preset.default}
      />
    );
  };

  const presetRows = filteredPresets.map(buildPresetRow);

  const listContent = match<[boolean, boolean, boolean], ReactNode>([
    isError,
    isLoading,
    filteredPresets.length > 0,
  ])
    .with([true, Pattern.any, Pattern.any], () => (
      <Box padding={3}>
        <Center>
          <Text size="small" color="secondary">
            {i18n.t('entity.sourcing.relativeFilters.dropdown.fetchError')}
          </Text>
        </Center>
        <Box paddingTop={2}>
          <Center>
            <RetryButton type="button" onClick={() => refetch()}>
              <Text size="small" alternative>
                {i18n.t('general.actions.retry')}
              </Text>
            </RetryButton>
          </Center>
        </Box>
      </Box>
    ))
    .with([false, true, Pattern.any], () => (
      <Box padding={4}>
        <Center>
          <Spinner />
        </Center>
      </Box>
    ))
    .with([false, false, false], () => (
      <Box padding={3}>
        <Center>
          <Text size="small" color="secondary">
            {i18n.t('general.labels.noData')}
          </Text>
        </Center>
      </Box>
    ))
    .otherwise(() => presetRows);

  return (
    <Box data-testid={suffixTestId('adServerRelativeFiltersDropdown', props)}>
      <PanelLayout>
        <SearchRegion onKeyDown={handleDropdownStickyRegionKeyDown}>
          <Search
            value={searchQuery}
            onChange={setSearchQuery}
            placeholder={i18n.t('general.labels.searchPlaceholder')}
            data-testid={suffixTestId('search', props)}
          />
        </SearchRegion>
        <ListRegion>
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            modifiers={[restrictToVerticalAxis]}
            onDragEnd={handleDragEnd}
          >
            <SortableContext items={sortablePresetIds} strategy={verticalListSortingStrategy}>
              {listContent}
            </SortableContext>
          </DndContext>
        </ListRegion>
        <AdServerRelativeFiltersDropdownFooter
          label={i18n.t('entity.sourcing.relativeFilters.actions.createPreset')}
          isDisabled={isError}
          onClick={handleCreatePreset}
          data-testid={props['data-testid']}
        />
      </PanelLayout>
    </Box>
  );
}

const PanelLayout = styled.div`
  display: flex;
  flex-direction: column;
  width: ${({theme}) => theme.getSize(66)};
  min-width: ${({theme}) => theme.getSize(66)};
  max-height: min(
    ${({theme}) => theme.getSize(105)},
    calc(100vh - ${({theme}) => theme.getSize(20)})
  );
  overflow: visible;
`;

const SearchRegion = styled.div`
  flex-shrink: 0;
  padding: ${({theme}) => theme.getSize(1)} 0 ${({theme}) => theme.getSize(2)};
  border-bottom: 1px solid ${({theme}) => theme.colors.general.separator};
  background-color: ${({theme}) => theme.colors.general.white};
`;

const ListRegion = styled.div`
  flex: 1 1 auto;
  min-height: 0;
  overflow-x: hidden;
  overflow-y: auto;
`;

const RetryButton = styled.button`
  border: none;
  background: transparent;
  padding: ${({theme}) => theme.getSize(1)};
  cursor: pointer;
  font: inherit;
  color: inherit;

  &:hover {
    text-decoration: underline;
  }
`;
